/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from "./Header/header.styles";
import IsOfflineBar from "./IsOfflineBar";
import { OfflineBarHeightContext } from "./offlineBarHeight";
import { breakpoints } from "../../../constants/mediaqueries";

interface LayoutProps {
    children: React.ReactNode;
    header?: React.ReactNode;
    fullscreen?: boolean;
    bgWhite?: boolean;
}

const fixedHeaderStyle = (visible: boolean, topOffset: number) => css`
    position: fixed;
    top: ${topOffset}px;
    left: 0;
    right: 0;
    transition: transform 0.3s ease;
    transform: translateY(${visible ? "0" : "-100%"});
    z-index: 1000;

    height: ${HEADER_HEIGHT_MOBILE};

    @media (min-width: ${breakpoints[2]}px) {
        height: ${HEADER_HEIGHT};
    }
`;

// Banner sits in its own fixed wrapper above the header. Higher z-index
// than the header so the header's hide-on-scroll transform tucks behind
// it cleanly. The wrapper's height is observed (collapses to 0 when the
// banner renders nothing) and used to push down the header + body so
// nothing overlaps.
const offlineBarWrapperStyle = css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
`;

export default function DefaultLayout({ children, header, fullscreen = false, bgWhite }: LayoutProps) {
    const [visible, setVisible] = useState(true);
    const lastScrollY = useRef(0);

    const offlineBarRef = useRef<HTMLDivElement>(null);
    const [offlineBarHeight, setOfflineBarHeight] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY;

            if (currentY > lastScrollY.current && currentY > 56) {
                setVisible(false);
            } else {
                setVisible(true);
            }

            lastScrollY.current = currentY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Track the banner wrapper's height so the header and body shift by
    // exactly that amount when the banner appears/disappears. Wrapper is
    // always mounted; its height is 0 when IsOfflineBar returns null.
    useEffect(() => {
        const el = offlineBarRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            setOfflineBarHeight(entry.contentRect.height);
        });
        ro.observe(el);
        setOfflineBarHeight(el.getBoundingClientRect().height);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            className={`d-flex flex-column justify-content-between ${bgWhite ? "" : "bg-gray"}`}
            css={[css`min-height: 100vh;`]}
        >
            <div ref={offlineBarRef} css={offlineBarWrapperStyle}>
                <IsOfflineBar />
            </div>

            {!fullscreen && (header ?? <div css={fixedHeaderStyle(visible, offlineBarHeight)}><Header /></div>)}

            <div
                className="d-flex flex-column flex-grow-1"
                css={css`
                    margin-top: calc(${HEADER_HEIGHT_MOBILE} + ${offlineBarHeight}px);
                    @media (min-width: ${breakpoints[2]}px) {
                        margin-top: calc(${HEADER_HEIGHT} + ${offlineBarHeight}px);
                    }
                `}
            >
                {/* Published so page-level fixed elements can take the same
                    offset the header takes, instead of measuring the banner a
                    second time. See offlineBarHeight.ts. */}
                <OfflineBarHeightContext.Provider value={offlineBarHeight}>
                    {children}
                </OfflineBarHeightContext.Provider>
            </div>

            <div className={bgWhite ? "" : "bg-gray"}>
                <Footer />
            </div>
        </div>
    );
}
